import React, { useState } from 'react';
import { STRICT_LANGUAGES } from '../constants';
import { useTranslation } from 'react-i18next';
import { X, Save, FileUp } from 'lucide-react';
import T from './T';
import i18nInstance from '../i18n';


interface TxtImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (category: string, langCode: string) => void;
  existingDecks: { id: string, name: string }[];
}

export const TxtImportModal: React.FC<TxtImportModalProps> = ({ isOpen, onClose, onConfirm, existingDecks }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  
  // States
  const [selectedDeck, setSelectedDeck] = useState<string>(existingDecks[0]?.id || '');
  const [newDeckName, setNewDeckName] = useState('');
  const [selectedLang, setSelectedLang] = useState('en');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [showError, setShowError] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setNewDeckName('');
    setShowError(false);
    onClose();
  };

  const handleConfirm = () => {
    if (mode === 'existing') {
      if (!selectedDeck) return;
      onConfirm(selectedDeck, '');
      handleClose();
    } else {
      if (!newDeckName.trim()) return;
      onConfirm(newDeckName.trim(), selectedLang);
      handleClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[6000] flex items-center justify-center p-6 bg-black/30 backdrop-blur-sm animate-in fade-in duration-200" onClick={handleClose}>
      <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[90vw] max-w-md text-center animate-in zoom-in-95 duration-200 flex flex-col relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={handleClose} className="absolute top-6 left-6 text-muted opacity-50 hover:opacity-100 transition-opacity z-10">
          <X size={24} />
        </button>
        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shrink-0 mt-2 text-primary">
           <FileUp size={32} />
        </div>
        <h3 className="text-xl font-semibold text-ink leading-tight mb-2 max-w-[200px] mx-auto">
          Importar baralho de flashcards
        </h3>

        <div className="space-y-4">
          <p className="text-sm text-muted font-medium"><T k="ui.txt.where" /></p>
          
          <div className="flex gap-2 p-1 bg-tint-stone/60 rounded-2xl">
            <button
              onClick={() => setMode('existing')}
              className={`flex-1 py-3 text-xs font-normal uppercase font-mono tracking-[0.14em] rounded-xl transition-all ${mode === 'existing' ? 'bg-surface text-primary' : 'text-muted'}`}
            >
              Existente
            </button>
            <button
              onClick={() => setMode('new')}
              className={`flex-1 py-3 text-xs font-normal uppercase font-mono tracking-[0.14em] rounded-xl transition-all ${mode === 'new' ? 'bg-surface text-primary' : 'text-muted'}`}
            >
              Novo
            </button>
          </div>

          {mode === 'existing' ? (
            <div className="space-y-2 mt-4">
              <label className="text-xs font-normal uppercase font-mono text-muted tracking-[0.14em]">Baralho Existente</label>
              {existingDecks.length > 0 ? (
                <select
                  value={selectedDeck}
                  onChange={(e) => setSelectedDeck(e.target.value)}
                  className="w-full bg-tint-stone/40 text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-2xl px-4 py-4 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all font-medium appearance-none"
                >
                  {existingDecks.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              ) : (
                <p className="text-sm text-warn font-medium"><T k="ui.txt.noDeck" /></p>
              )}
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-[10px] font-normal uppercase font-mono text-muted tracking-[0.14em] px-2 mb-2 block text-left"><T k="ui.txt.newDeckName" /></label>
                <input
                  type="text"
                  maxLength={15}
                  value={newDeckName}
                  onChange={(e) => { setNewDeckName(e.target.value); setShowError(false); }}
                  placeholder={i18nInstance.t('ui.txt.deckPlaceholder')}
                  className={`w-full h-14 bg-tint-stone/40 text-ink border rounded-2xl px-4 text-sm font-bold outline-none transition-all ${showError && !newDeckName.trim() ? 'border-bad' : 'border-transparent focus:border-primary/80'}`}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-normal uppercase font-mono text-muted tracking-[0.14em]"><T k="ui.txt.cardsLang" /></label>
                <div className="relative">
                  <div 
                    onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                    className="w-full bg-tint-stone/40 text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-2xl px-4 py-4 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all font-medium flex items-center justify-between cursor-pointer"
                  >
                    <span>{STRICT_LANGUAGES.find(l => l.code === selectedLang)?.name || 'Selecione'}</span>
                    <svg className={`w-5 h-5 text-muted transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                  
                  {isLangDropdownOpen && (
                    <div className="absolute top-full left-0 w-full mt-2 max-h-56 overflow-y-auto bg-surface border border-black/[0.06] rounded-2xl z-50 animate-in fade-in zoom-in-95 duration-100 py-2">
                      {STRICT_LANGUAGES.map(lang => (
                        <div 
                          key={lang.code}
                          onClick={() => {
                            setSelectedLang(lang.code);
                            setIsLangDropdownOpen(false);
                          }}
                          className={`px-4 py-3 cursor-pointer text-sm font-medium transition-colors ${selectedLang === lang.code ? 'bg-primary/10 text-primary dark:text-primary' : 'text-ink hover:bg-tint-stone/40 '}`}
                        >
                          {lang.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => {
             if (mode === 'new' && !newDeckName.trim()) {
                 setShowError(true);
             } else {
                 handleConfirm();
             }
          }}
          disabled={mode === 'existing' && !selectedDeck}
          className="mt-6 w-full py-4 bg-primary text-slate-900 rounded-2xl font-medium text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
        >
          Confirmar e criar!
        </button>
      </div>
    </div>
  );
};

